import { useRef, useState, useEffect, useCallback } from 'react';
import { ScrollDirection } from './Carousel.types';

const initialScrollState = {
    canScrollLeft: false,
    canScrollRight: false,
    shouldCenter: false,
};

const scrollCoef = 0.5;

export const useCarousel = <T>(items: T[]) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [scrollState, setScrollState] = useState(initialScrollState);

    const checkScrollability = useCallback(() => {
        const container = scrollContainerRef.current;

        if (!container) {
            return;
        }

        const { scrollLeft, scrollWidth, clientWidth } = container;
        const needsScrolling = scrollWidth > clientWidth;

        if (!needsScrolling) {
            setScrollState({
                canScrollLeft: false,
                canScrollRight: false,
                shouldCenter: true,
            });

            return;
        }

        const isAtStart = scrollLeft <= 1;
        const isAtEnd = scrollLeft >= scrollWidth - clientWidth - 1;

        setScrollState({
            canScrollLeft: !isAtStart,
            canScrollRight: !isAtEnd,
            shouldCenter: false,
        });
    }, []);

    const scroll = useCallback((direction: ScrollDirection) => {
        const container = scrollContainerRef.current;

        if (!container) {
            return;
        }

        const scrollAmount = container.clientWidth * scrollCoef;
        container.scrollBy({
            left: direction === ScrollDirection.Left ? -scrollAmount : scrollAmount,
            behavior: 'smooth',
        });
    }, []);

    const handleScrollLeft = useCallback(() => scroll(ScrollDirection.Left), [scroll]);
    const handleScrollRight = useCallback(() => scroll(ScrollDirection.Right), [scroll]);

    useEffect(() => {
        const container = scrollContainerRef.current;

        if (!container) {
            return;
        }

        checkScrollability();

        container.addEventListener('scroll', checkScrollability, { passive: true });
        window.addEventListener('resize', checkScrollability, { passive: true });

        return () => {
            container.removeEventListener('scroll', checkScrollability);
            window.removeEventListener('resize', checkScrollability);
        };
    }, [checkScrollability]);

    useEffect(checkScrollability, [items, checkScrollability]);

    return {
        ...scrollState,
        scrollContainerRef,
        handleScrollLeft,
        handleScrollRight,
    };
};
